import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import {
  Layout as BaseLayout,
  Button,
  Dropdown,
  Flex,
  MenuProps,
  message,
  Modal,
  Row,
  Space,
  Typography,
} from 'antd';
import ConsultantDetailed from '@/components/admin/detailed/ConsultantDetailed';
import { ROUTES } from '@/types/routes';
import BookingsTable from '@/components/admin/bookings';
import { BookingAdmin, BookingStatus, Consultant, IReview } from '@/types';
import { getConsultantByIdAdmin } from '@/api/consultants';
import { getAdminBookings } from '@/api/booking';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { updateUser, deleteUser, downloadBookings } from '@/api/admin';
import { DownOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { getBookingStatusLabel } from '@/utils/status';

const columns = [
  {
    title: 'ID',
    dataIndex: '_id',
    key: 'id',
    render: (text: string) => (
      <Typography.Link style={{ fontWeight: 500, color: '#1C8271' }}>
        {text}
      </Typography.Link>
    ),
  },
  {
    title: 'Franchisor',
    dataIndex: 'franchise',
    key: 'franchise',
    render: (franchisor: { _id: string; company: string }) => (
      <Link
        style={{ fontWeight: 500, color: '#1C8271' }}
        to={`${ROUTES.ADMIN_FRANCHISES}/${franchisor._id}`}
      >
        {franchisor.company}
      </Link>
    ),
  },
  {
    title: 'Date',
    dataIndex: 'start',
    key: 'start',
    render: (date: string) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Typography.Text>
          {date ? format(new Date(date), 'd MMM, yyyy, h:mm a') : 'N/A'}
        </Typography.Text>
      </div>
    ),
  },
  {
    title: 'Marked As Completed',
    dataIndex: 'markedAsCompletedAt',
    key: 'markedAsCompletedAt',
    render: (
      date: string,
      record: { updatedAt: string; status: BookingStatus },
    ) => {
      // Legacy completed bookings predate markedAsCompletedAt; fall back to
      // updatedAt so they still show a date. The CSV export mirrors this
      // exact fallback (TCX-574).
      const completedAt = date || record.updatedAt;
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Typography.Text>
            {record.status === BookingStatus.COMPLETED && completedAt
              ? format(new Date(completedAt), 'd MMM, yyyy, h:mm a')
              : 'N/A'}
          </Typography.Text>
        </div>
      );
    },
  },
  {
    title: 'Duration',
    dataIndex: 'duration',
    key: 'duration',
    render: (duration: number) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Typography.Text>{duration} min</Typography.Text>
      </div>
    ),
  },

  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: (status: 'pending' | 'declined' | 'completed' | 'future') => {
      const statusColors = {
        pending: '#FFA500',
        declined: '#F68B8B',
        completed: '#1C8271',
        future: '#2A4BC2',
      };
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: statusColors[status],
            }}
          />
          <Typography.Text>
            {getBookingStatusLabel(status)}
          </Typography.Text>
        </div>
      );
    },
  },

  {
    title: 'Rate',
    dataIndex: 'review',
    key: 'rate',
    render: (item: IReview[]) => (item?.[0]?.rating ? item[0].rating : '-'),
  },

  {
    // Mixed-direction row (TCX-574): Rate above is the franchisor→consultant
    // score, but Additional Feedback is the comment "left by the consultant"
    // (consultant→franchisor). The server returns it as a scalar
    // `additionalFeedback` so the two columns read different review directions.
    title: 'Additional Feedback',
    dataIndex: 'additionalFeedback',
    key: 'additionalFeedback',
    width: 320,
    render: (text: string) => text || '-',
  },
];

export default function AdminConsultantsDetailedPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const queryClient = useQueryClient();
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isHideModalOpen, setIsHideModalOpen] = useState(false);
  const navigate = useNavigate();

  const { data: consultant } = useQuery<Consultant | null, Error>({
    queryKey: ['consultant', id],
    queryFn: () => getConsultantByIdAdmin(id || ''),
  });

  const { data } = useQuery<
    {
      total: number;
      page: number;
      limit: number;
      sessions: BookingAdmin[];
    },
    Error
  >({
    queryKey: ['bookings', searchParams.toString(), page, limit],
    queryFn: () =>
      getAdminBookings({
        startDate: searchParams.get('startDate') || '',
        endDate: searchParams.get('endDate') || '',
        search: searchParams.get('search') || '',
        consultantId: id,
        page,
        limit,
      }),
  });

  // Note: previously fetched a 10,000-row bookings list here just to
  // compute averageRating client-side, but the result was date-filtered
  // by `searchParams` and silently disagreed with the CSV export.
  // The server now computes `averageRating` on the consultant detail
  // endpoint using the same logic as the export (TCX-568); we just
  // read `consultant.averageRating` directly below.

  const { mutateAsync: deactivateFranchise, isPending: isDeactivating } =
    useMutation({
      mutationKey: ['deactivateFranchise'],
      mutationFn: async (id: string) => {
        const res = await updateUser({
          data: {
            isDeactivated: consultant?.isDeactivated ? false : true,
          },
          id,
        });
        return res;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['consultant', id],
        });
        message.success(
          `User ${
            consultant?.isDeactivated ? 'activated' : 'deactivated'
          } successfully`,
        );
      },
    });

  const { mutateAsync: hideFranchise, isPending: isHiding } = useMutation({
    mutationKey: ['hideFranchise'],
    mutationFn: async (id: string) => {
      const res = await updateUser({
        data: {
          status: consultant?.status ? false : true,
        },
        id,
      });
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['consultant', id],
      });
      message.success(
        `User ${consultant?.status ? 'hidden' : 'shown'} successfully`,
      );
    },
  });

  const { mutateAsync: deleteFranchiseMutation, isPending: isDeleting } =
    useMutation({
      mutationKey: ['deleteFranchise'],
      mutationFn: async (id: string) => {
        const res = await deleteUser(id);
        return res;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['consultant', id],
        });
        message.success(`User deleted successfully`);
      },
    });

  const { mutateAsync: downloadBookingsMutate, isPending } = useMutation({
    mutationFn: () =>
      downloadBookings({
        startDate: searchParams.get('startDate') || '',
        endDate: searchParams.get('endDate') || '',
        search: searchParams.get('search') || '',
        consultantId: id,
      }),
    // Single source of truth for HTTP-failure toasts. handleDownloadTable
    // catches the rejection but does NOT toast again — see TCX-540, where
    // both this callback AND the catch block were firing message.error,
    // producing two identical toasts per failed click.
    onError: (error: any) => {
      console.error('downloadBookings failed:', error);
      message.error(
        error?.message || 'Bookings download failed. Please try again.',
      );
    },
  });

  const handleDownloadTable = async () => {
    let result: { file?: string };
    try {
      result = await downloadBookingsMutate();
    } catch {
      // Mutation rejected — onError already surfaced the toast. Bail silently
      // so we don't double-toast.
      return;
    }

    const base64Response = result?.file;
    if (!base64Response || typeof base64Response !== 'string') {
      message.error('Bookings download failed: server returned an invalid file.');
      return;
    }

    try {
      const csvContent = atob(base64Response);
      const blob = new Blob([csvContent], { type: 'text/csv' });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `bookings_${new Date().toISOString()}.csv`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      message.success('File downloaded successfully');
    } catch (error: any) {
      console.error('Bookings CSV save failed:', error);
      message.error(error?.message || 'Failed to save file.');
    }
  };

  const items: MenuProps['items'] = [
    {
      label: consultant?.isDeactivated ? 'Activate User' : 'Deactivate User',
      key: '1',
      disabled: isDeactivating,
    },
    {
      label: consultant?.status ? 'Hide User' : 'Show User',
      key: '2',
      disabled: isHiding,
    },
    {
      label: 'Delete User',
      key: '3',
      danger: true,
      disabled: isDeleting,
    },
  ];

  const handleEditUser = async () => {
    navigate(`/control-panel/consultants/${id}/edit`);
  };

  const handleDeactivateOk = async () => {
    if (!id) return;
    await deactivateFranchise(id);
    setIsDeactivateModalOpen(false);
  };

  const handleCancel = () => {
    setIsDeactivateModalOpen(false);
  };

  const handleDeleteOk = async () => {
    if (!id) return;
    await deleteFranchiseMutation(id);
    setIsDeleteModalOpen(false);
  };

  const handleHideOk = async () => {
    if (!id) return;
    await hideFranchise(id);
    setIsHideModalOpen(false);
  };

  const handleHideCancel = () => {
    setIsHideModalOpen(false);
  };
  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
  };

  const handleMenuClick: MenuProps['onClick'] = async (e) => {
    if (e.key === '1') {
      setIsDeactivateModalOpen(true);
    }
    if (e.key === '2') {
      setIsHideModalOpen(true);
    }
    if (e.key === '3') {
      setIsDeleteModalOpen(true);
    }
  };

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };

  // Server-computed, all-time, matches the CSV export and public listing
  // (TCX-568, TCX-601). totalReviews drives the "(N reviews)" suffix
  // on the rating row; the star + numeric value render unconditionally
  // because the BE returns a 4.0 placeholder for unrated consultants
  // (TCX-613 product decision).
  const averageRating = consultant?.averageRating ?? 4;
  const totalReviews = consultant?.totalReviews ?? 0;

  return (
    <>
      <Flex justify="space-between" align="center" gap={24}>
        <div>
          <Typography.Title
            level={2}
            style={{
              margin: '0 0 24px',
            }}
          >
            Consultant Detail
          </Typography.Title>
          <Row className="tables-info">
            <Typography.Text style={{ color: '#9FA1AD', fontWeight: 500 }}>
              Consultant Management
            </Typography.Text>
            <span className="tables-divider" />
            <Typography.Text style={{ color: '#9FA1AD', fontWeight: 500 }}>
              Consultant Detail
            </Typography.Text>
          </Row>
        </div>
        <Flex align="center" justify="center" gap={24}>
          <Dropdown menu={menuProps}>
            <Button
              type="primary"
              style={{
                backgroundColor: 'white',
                color: '#1C8271',
                boxShadow: 'none',
                height: '40px',
              }}
            >
              <Space>
                Actions
                <DownOutlined />
              </Space>
            </Button>
          </Dropdown>
          <Button
            type="primary"
            className="admin-button-download-table"
            onClick={handleEditUser}
          >
            Edit User
          </Button>
        </Flex>
      </Flex>
      <BaseLayout>
        <BaseLayout.Sider
          style={{ height: '100%', minHeight: '100vh' }}
          width={'25%'}
        >
          {consultant && (
            <ConsultantDetailed
              consultant={consultant}
              rating={averageRating}
              totalReviews={totalReviews}
            />
          )}
        </BaseLayout.Sider>
        <BaseLayout.Content style={{ padding: '0 16px' }}>
          <BookingsTable
            data={data?.sessions || []}
            total={data?.total || 0}
            columns={columns}
            isDetailed={true}
            page={page}
            setPage={setPage}
            limit={limit}
            setLimit={setLimit}
            downloadData={handleDownloadTable}
            scroll={{ x: 'max-content' }}
          />
        </BaseLayout.Content>
      </BaseLayout>
      <Modal
        title={consultant?.isDeactivated ? 'Activate User' : 'Deactivate User'}
        open={isDeactivateModalOpen}
        onOk={handleDeactivateOk}
        onCancel={handleCancel}
      >
        <div className={'modal-section-line'}>
          <Typography.Text className="general-info-text">
            {` Are you sure you want to ${
              consultant?.isDeactivated ? 'activate' : 'deactivate'
            } this user?`}
          </Typography.Text>
        </div>
      </Modal>
      <Modal
        title={consultant?.status ? 'Hide User' : 'Show User'}
        open={isHideModalOpen}
        onOk={handleHideOk}
        onCancel={handleHideCancel}
      >
        <div className={'modal-section-line'}>
          <Typography.Text className="general-info-text">
            {` Are you sure you want to ${
              consultant?.status ? 'hide' : 'show'
            } this user?`}
          </Typography.Text>
        </div>
      </Modal>
      <Modal
        title="Delete User?"
        open={isDeleteModalOpen}
        onOk={handleDeleteOk}
        onCancel={handleDeleteCancel}
      >
        <div className={'modal-section-line'}>
          <Typography.Text className="general-info-text">
            Are you sure you want to delete this user?
          </Typography.Text>
        </div>
      </Modal>
    </>
  );
}
